/**
 * Which PDF.js build this browser can run. PDF.js 6's modern build uses recent JavaScript without
 * polyfills and is meant for the latest Firefox and Chrome only (PDF.js FAQ); Safari, iOS Safari above
 * all, lacks several of these. The legacy build polyfills them (PDF.js: Safari 18+ "mostly"; its
 * remaining syntax needs class static blocks, Safari 16.4+). Both builds are loaded lazily, with the
 * same load options and the same worker CSP.
 */

/** Features the modern build calls directly (checked in pdfjs-dist 6.4.299's build/pdf.mjs and pdf.worker.mjs). */
export function missingModernFeatures(g: typeof globalThis = globalThis): string[] {
  const missing: string[] = [];
  const has = (ok: boolean, name: string) => {
    if (!ok) missing.push(name);
  };
  const MapProto = g.Map?.prototype as unknown as Record<string, unknown> | undefined;
  const U8 = g.Uint8Array as unknown as Record<string, unknown> & { prototype: Record<string, unknown> };
  has(typeof MapProto?.getOrInsert === 'function', 'Map.prototype.getOrInsert');
  has(typeof MapProto?.getOrInsertComputed === 'function', 'Map.prototype.getOrInsertComputed');
  has(typeof (g.Math as unknown as Record<string, unknown>)?.sumPrecise === 'function', 'Math.sumPrecise');
  has(typeof (g.Promise as unknown as Record<string, unknown>)?.try === 'function', 'Promise.try');
  has(typeof (g.Promise as unknown as Record<string, unknown>)?.withResolvers === 'function', 'Promise.withResolvers');
  has(typeof U8?.prototype?.toHex === 'function', 'Uint8Array.prototype.toHex');
  has(typeof U8?.fromBase64 === 'function', 'Uint8Array.fromBase64');
  has(typeof (g.URL as unknown as Record<string, unknown>)?.parse === 'function', 'URL.parse');
  has(typeof (g as unknown as Record<string, unknown>).Float16Array === 'function', 'Float16Array');
  return missing;
}

/**
 * WebKit (every iOS browser, Safari on the Mac): Apple's engine whatever the feature checks say. PDF.js
 * recommends its legacy build for Safari, and an iPhone on the latest iOS has every feature above yet
 * failed on every PDF (2026-10-11) while the legacy build passed in Playwright's WebKit. iOS Chrome,
 * Firefox and Edge are WebKit too ("CriOS", "FxiOS", "EdgiOS"); Chromium browsers say "Chrome/".
 */
export const isAppleWebKit = (ua: string): boolean => /AppleWebKit\//.test(ua) && !/(Chrome|Chromium|HeadlessChrome)\//.test(ua);

const userAgent = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent);

export const needsLegacyPdfjs = (g: typeof globalThis = globalThis, ua: string = userAgent()) => isAppleWebKit(ua) || missingModernFeatures(g).length > 0;

/** Why the legacy build was chosen, for the debug panel. */
export const legacyReason = (g: typeof globalThis = globalThis, ua: string = userAgent()): string[] => [
  ...(isAppleWebKit(ua) ? ['Apple WebKit engine'] : []),
  ...missingModernFeatures(g).map((f) => `missing ${f}`),
];

/**
 * Why PDF.js couldn't read a file: the file itself is malformed (PDF.js's own parse errors), or the
 * reader failed in this browser (a missing feature, a worker that couldn't start…). Only the first is
 * "damaged"; the second asks the user to update the browser or try another one.
 */
export function readerFailureKind(err: unknown): 'malformed' | 'reader' {
  const name = typeof err === 'object' && err !== null ? (err as { name?: unknown }).name : undefined;
  return name === 'InvalidPDFException' || name === 'FormatError' ? 'malformed' : 'reader';
}
